import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import { createTheme, ThemeProvider, CssBaseline, GlobalStyles } from '@mui/material';

interface ThemeContextType {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useThemeContext must be used within ThemeContextProvider');
  return context;
};

function getStoredMode(): 'light' | 'dark' {
  try {
    const stored = localStorage.getItem('theme-mode');
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage unavailable (e.g. private browsing) — fall through
  }
  return 'light';
}

export const ThemeContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>(getStoredMode);

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem('theme-mode', next);
      } catch {
        // localStorage unavailable — skip persistence
      }
      return next;
    });
  };

  const headingFont = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  const bodyFont = '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: '#3B82F6' },
          secondary: { main: '#4338CA' },
          ...(mode === 'dark'
            ? {
                background: { default: '#0F0F23', paper: '#1B1B30' },
                text: { primary: '#F8FAFC', secondary: '#94A3B8' },
                divider: 'rgba(255,255,255,0.08)',
              }
            : {
                background: { default: '#ffffff', paper: '#F8FAFC' },
                text: { primary: '#0F172A', secondary: '#64748B' },
                divider: 'rgba(0,0,0,0.08)',
              }),
        },
        typography: {
          fontFamily: bodyFont,
          h1: { fontFamily: headingFont, fontWeight: 700, letterSpacing: '-0.02em' },
          h2: { fontFamily: headingFont, fontWeight: 700, letterSpacing: '-0.02em' },
          h3: { fontFamily: headingFont, fontWeight: 700, letterSpacing: '-0.01em' },
          h4: { fontFamily: headingFont, fontWeight: 600, letterSpacing: '-0.01em' },
          h5: { fontFamily: headingFont, fontWeight: 600 },
          h6: { fontFamily: headingFont, fontWeight: 600 },
        },
      }),
    [mode],
  );

  const value = useMemo(() => ({ mode, toggleTheme }), [mode]);

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles styles={{
          'button:focus-visible, a:focus-visible, input:focus-visible': {
            outline: '2px solid #3B82F6',
            outlineOffset: '2px',
          },
          'button:active:not(:disabled)': {
            transform: 'scale(0.97)',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
            },
          },
        }} />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
